<template>
  <div class="rich-text-area">
    <div class="rich-text-area__header">
      <bg-text size="title-5">{{ label }}</bg-text>

      <div class="rich-text-area__toolbar">
        <div v-for="action in TOOLBAR_ACTIONS" :key="action.command">
          <div
            v-if="action.separator"
            class="rich-text-area__toolbar-separator"
            data-testid="toolbar-separator"
          />

          <button
            v-else
            type="button"
            class="rich-text-area__toolbar-btn"
            :class="{
              'rich-text-area__toolbar-btn--active':
                activeFormats[action.command],
            }"
            :title="action.title"
            @mousedown.prevent="execCommand(action.command)"
          >
            <img
              v-if="action.iconSrc"
              :src="action.iconSrc"
              :alt="action.title"
              width="16"
              height="16"
            />

            <span v-else v-html="action.icon" />
          </button>
        </div>
      </div>
    </div>

    <div
      ref="editor"
      class="rich-text-area__editor"
      :class="{ 'rich-text-area__editor--disabled': disabled }"
      :contenteditable="!disabled"
      data-testid="rich-text-editor"
      :placeholder="placeholder"
      @input="handleInput"
      @keydown="handleKeydown"
      @keyup="updateActiveFormats"
      @mouseup="updateActiveFormats"
      @paste="handlePaste"
    />

    <div class="rich-text-area__footer">
      <bg-text size="label-4" class="bg-u-text-neutral-500">
        {{ charCountDisplay }}
      </bg-text>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, computed } from '@vue/composition-api';
import { BgText } from 'bangul-vue';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

import unorderedListIconSrc from './icons/unordered-list.svg';
import orderedListIconSrc from './icons/ordered-list.svg';

const TOOLBAR_ACTIONS = [
  { command: 'bold', title: 'Bold', icon: '<b>B</b>' },
  { command: 'italic', title: 'Italic', icon: '<i>I</i>' },
  {
    command: 'underline',
    title: 'Underline',
    icon: '<u>U</u>',
  },
  { separator: true },
  {
    command: 'insertUnorderedList',
    title: 'Bullet List',
    iconSrc: unorderedListIconSrc,
  },
  {
    command: 'insertOrderedList',
    title: 'Numbered List',
    iconSrc: orderedListIconSrc,
  },
];

const BLOCK_SELECTOR = 'div, p';

/*
 * Browsers represent new lines in contenteditable with block wrappers
 * (<div>/<p>), but the sanitizer only allows inline tags + <br>, so those
 * wrappers would be stripped and the lines would collapse. Convert each block
 * boundary to a <br> before sanitizing, leaving lists (ul/ol/li) untouched.
 */
const normalizeBlocksToBr = editorEl => {
  const container = editorEl.cloneNode(true);

  let block = container.querySelector(BLOCK_SELECTOR);

  while (block) {
    block.parentNode.insertBefore(document.createElement('br'), block);

    /*
     * An otherwise-empty block only holds the browser's filler <br>; the
     * boundary break above already represents that blank line, so drop it.
     */
    const isFillerOnly =
      block.childNodes.length === 1 && block.firstChild.nodeName === 'BR';

    if (!isFillerOnly) {
      while (block.firstChild) {
        block.parentNode.insertBefore(block.firstChild, block);
      }
    }

    block.parentNode.removeChild(block);
    block = container.querySelector(BLOCK_SELECTOR);
  }

  return container.innerHTML;
};

export default {
  name: 'RichTextArea',

  components: {
    BgText,
  },

  props: {
    value: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const editor = ref(null);
    const charCount = ref(0);
    const activeFormats = reactive({
      bold: false,
      italic: false,
      underline: false,
      insertUnorderedList: false,
      insertOrderedList: false,
    });

    const charCountDisplay = computed(() => {
      const charCountFormatted = charCount.value.toLocaleString('id-ID');
      const maxLengthFormatted = props.maxLength.toLocaleString('id-ID');

      return `${charCountFormatted}/${maxLengthFormatted}`;
    });

    const getTextLength = element => {
      if (!element) return 0;
      return element.innerText.replace(/\n/g, '').length;
    };

    const updateCharCount = () => {
      charCount.value = getTextLength(editor.value);
    };

    const getLastTextNode = node => {
      if (node.nodeType === Node.TEXT_NODE) return node;

      for (let i = node.childNodes.length - 1; i >= 0; i--) {
        const found = getLastTextNode(node.childNodes[i]);

        if (found) return found;
      }

      return null;
    };

    const truncateContent = (element, maxLen) => {
      while (getTextLength(element) > maxLen) {
        const lastText = getLastTextNode(element);

        if (!lastText) break;

        const overflow = getTextLength(element) - maxLen;

        if (lastText.textContent.length > overflow) {
          lastText.textContent = lastText.textContent.slice(0, -overflow);
        } else {
          lastText.parentNode.removeChild(lastText);
        }
      }

      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const handleInput = () => {
      if (!editor.value) return;

      if (getTextLength(editor.value) > props.maxLength) {
        truncateContent(editor.value, props.maxLength);
      }

      updateCharCount();

      const html =
        charCount.value === 0
          ? ''
          : sanitizeHtml(normalizeBlocksToBr(editor.value));

      emit('input', html);
    };

    const handlePaste = e => {
      e.preventDefault();

      const text = (e.clipboardData || window.clipboardData).getData(
        'text/plain'
      );

      document.execCommand('insertText', false, text);
    };

    const handleKeydown = e => {
      if (e.key === 'Enter' && getTextLength(editor.value) >= props.maxLength) {
        e.preventDefault();
      }
    };

    const updateActiveFormats = () => {
      activeFormats.bold = document.queryCommandState('bold');
      activeFormats.italic = document.queryCommandState('italic');
      activeFormats.underline = document.queryCommandState('underline');
      activeFormats.insertUnorderedList = document.queryCommandState(
        'insertUnorderedList'
      );
      activeFormats.insertOrderedList = document.queryCommandState(
        'insertOrderedList'
      );
    };

    const execCommand = command => {
      document.execCommand(command, false, null);
      editor.value?.focus();
      updateActiveFormats();
      handleInput();
    };

    onMounted(() => {
      if (props.value && editor.value) {
        editor.value.innerHTML = sanitizeHtml(props.value);

        updateCharCount();
      }
    });

    return {
      TOOLBAR_ACTIONS,
      editor,
      charCount,
      activeFormats,
      charCountDisplay,
      handleInput,
      handleKeydown,
      handlePaste,
      updateActiveFormats,
      execCommand,
    };
  },
};
</script>

<style lang="scss" scoped src="./RichTextArea.scss" />
